import { useCallback, useEffect, useRef } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import {
  accessTokenNeedsRefresh,
  refreshAccessTokenOnce,
} from "../../services/auth-token-refresh";

const REFRESH_CHECK_INTERVAL_MS = 60_000;
const ASGARDEO_SESSION_DATA_PREFIX = "session_data-";

function hasPersistedRefreshToken() {
  try {
    return Object.keys(window.localStorage).some((key) => {
      if (!key.startsWith(ASGARDEO_SESSION_DATA_PREFIX)) {
        return false;
      }

      const session = JSON.parse(window.localStorage.getItem(key) || "{}");
      return typeof session.refresh_token === "string" && session.refresh_token.length > 0;
    });
  } catch {
    return false;
  }
}

function AuthSessionManager() {
  const { state, getAccessToken, refreshAccessToken } = useAuthContext();
  const checkingRef = useRef(false);
  const recoveryAttemptedRef = useRef(false);

  useEffect(() => {
    if (
      state.isLoading ||
      state.isAuthenticated ||
      recoveryAttemptedRef.current ||
      !hasPersistedRefreshToken()
    ) {
      return;
    }

    recoveryAttemptedRef.current = true;
    refreshAccessTokenOnce(refreshAccessToken)
      // The installed React SDK does not update its authentication state when
      // refreshAccessToken() is called from an initially signed-out state.
      // Reload once so AuthProvider initializes from the newly issued token.
      .then(() => window.location.reload())
      .catch((error) => {
        console.info("Unable to restore the persisted Asgardeo session:", error);
      });
  }, [refreshAccessToken, state.isAuthenticated, state.isLoading]);

  const refreshIfNeeded = useCallback(async () => {
    if (!state.isAuthenticated || checkingRef.current) {
      return;
    }

    checkingRef.current = true;
    try {
      const accessToken = await getAccessToken();
      if (accessTokenNeedsRefresh(accessToken)) {
        await refreshAccessTokenOnce(refreshAccessToken);
      }
    } catch (error) {
      console.error("Unable to refresh the Asgardeo access token:", error);
    } finally {
      checkingRef.current = false;
    }
  }, [getAccessToken, refreshAccessToken, state.isAuthenticated]);

  useEffect(() => {
    if (!state.isAuthenticated) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshIfNeeded();
      }
    };
    const handleForeground = () => void refreshIfNeeded();

    void refreshIfNeeded();
    const interval = window.setInterval(
      refreshIfNeeded,
      REFRESH_CHECK_INTERVAL_MS
    );
    window.addEventListener("focus", handleForeground);
    window.addEventListener("online", handleForeground);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", handleForeground);
      window.removeEventListener("online", handleForeground);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshIfNeeded, state.isAuthenticated]);

  return null;
}

export default AuthSessionManager;
